import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/auth";
import { useWorkspace } from "@/context/workspace";
import { slugify } from "@shared/utils/slugify";
import { safeRedirect } from "@shared/utils/safe-redirect";

export default function WorkspaceSetupPage() {
  const { user } = useAuth();
  const { memberships, createWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const redirect = safeRedirect(sp.get("redirect"), "/flows");

  const [name, setName] = useState("");
  const derivedSlug = useMemo(() => (name ? slugify(name) : ""), [name]);
  const [slug, setSlug] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (memberships.length > 0) navigate(redirect, { replace: true });
  }, [memberships.length, navigate, redirect]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setSubmitting(true);
    setError(null);
    try {
      await createWorkspace({ name: name.trim(), slug: (slug || derivedSlug || null) });
      navigate(redirect, { replace: true });
    } catch (e: any) {
      setError(e?.message ?? "Could not create workspace.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-sm mx-auto mt-16 p-4 rounded border bg-card">
      <h1 className="text-lg font-semibold mb-3">Create your workspace</h1>
      <form onSubmit={onSubmit} className="space-y-3">
        <div className="grid gap-1">
          <Label htmlFor="ws-name">Workspace name</Label>
          <Input id="ws-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="grid gap-1">
          <Label htmlFor="ws-slug">Slug (optional)</Label>
          <Input
            id="ws-slug"
            placeholder={derivedSlug || "my-team"}
            value={slug}
            onChange={(e) => setSlug(slugify(e.target.value))}
          />
          <p className="text-xs text-muted-foreground">Used in URLs. Leave empty to use the suggested slug.</p>
        </div>
        {error && <div className="text-sm text-destructive">{error}</div>}
        <div className="flex gap-2 items-center">
          <Button type="submit" disabled={submitting || !name.trim()}>
            {submitting ? "Creating…" : "Create workspace"}
          </Button>
        </div>
      </form>
    </div>
  );
}

